"use client";

import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import {
  postMessage,
  getThread,
  type SupportChatMessage,
} from "@/lib/support-chat/api";
import { useSupportChatSocket } from "@/lib/support-chat/socket";
import { MessageBubble } from "./message-bubble";
import { MessageInput } from "./message-input";
import { OnlineIndicator } from "./online-indicator";

type Props = {
  ticketId: number;
  sessionToken: string;
  visitorEmail?: string | null;
};

export function ChatPanel({ ticketId, sessionToken, visitorEmail }: Props) {
  const t = useTranslations("support_chat");
  const [messages, setMessages] = useState<SupportChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const { connected, agentsOnlineCount, staffTyping, sendVisitorTyping, onMessage } =
    useSupportChatSocket({ ticketId, sessionToken });

  useEffect(() => {
    let aborted = false;
    setLoading(true);
    getThread({ ticketId, sessionToken })
      .then((res) => {
        if (aborted) return;
        setMessages(res.messages);
        setLoading(false);
      })
      .catch((e) => {
        if (aborted) return;
        setError(e?.message || "Failed to load thread");
        setLoading(false);
      });
    return () => {
      aborted = true;
    };
  }, [ticketId, sessionToken]);

  useEffect(() => {
    return onMessage((msg) => {
      setMessages((prev) =>
        prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]
      );
    });
  }, [onMessage]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, staffTyping]);

  async function handleSend(body: string) {
    try {
      const res = await postMessage({ ticketId, sessionToken, body });
      setMessages((prev) =>
        prev.some((m) => m.id === res.message.id) ? prev : [...prev, res.message]
      );
    } catch (e: any) {
      setError(e?.message || "Send failed");
    }
  }

  return (
    <div className="flex flex-col h-full bg-white dark:bg-neutral-950 rounded-lg overflow-hidden">
      <header className="px-4 py-3 border-b border-neutral-200 dark:border-neutral-800 flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-neutral-900 dark:text-neutral-50">
            {t("header_title")}
          </div>
          <OnlineIndicator count={agentsOnlineCount} />
        </div>
      </header>

      {agentsOnlineCount === 0 && visitorEmail ? (
        <div className="px-3 py-2 bg-amber-50 dark:bg-amber-900/20 text-xs text-amber-800 dark:text-amber-200 border-b border-amber-200/50 dark:border-amber-800/30">
          {t("offline_email_banner", { email: visitorEmail })}
        </div>
      ) : null}

      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-3 py-3 space-y-2 bg-neutral-50 dark:bg-neutral-950"
      >
        {loading ? (
          <div className="text-xs text-center text-neutral-400 py-4">
            {t("loading")}
          </div>
        ) : null}
        {error ? (
          <div className="text-xs text-center text-red-500 py-2">{error}</div>
        ) : null}
        {messages.map((m) => (
          <MessageBubble key={m.id} message={m} />
        ))}
        {staffTyping ? (
          <div className="text-xs text-neutral-500 dark:text-neutral-400 italic px-2">
            {staffTyping.staffName
              ? t("staff_typing_named", { name: staffTyping.staffName })
              : t("staff_typing")}
          </div>
        ) : null}
        {!connected && !loading ? (
          <div className="text-xs text-center text-neutral-400 py-1">
            {t("reconnecting")}
          </div>
        ) : null}
      </div>

      <MessageInput onSend={handleSend} onTyping={sendVisitorTyping} />
    </div>
  );
}
